import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { SmeDashboardKpiApi } from '../tokens/sme-dashboard-kpi.token';
import {
  ComplianceSnapshot,
  ComplianceTone,
  DashboardKpis,
} from '../models/sme-dashboard.model';
import { API_PATHS } from '../config/api-paths';

/** Backend row shape from `GET /me/dashboard/compliance`. */
interface ComplianceRowResponse {
  key: string;
  status: string;
  tone: ComplianceTone;
}
interface ComplianceSnapshotResponse {
  rows: ComplianceRowResponse[];
}

/**
 * REST-only implementation of `SmeDashboardKpiApi` (EM-312).
 *
 * The four dashboard KPIs (Available Credit, Active Loans, Next Payment Due, V-IBAN
 * Balance) and the Compliance & Status snapshot belong to the Payments / Financing
 * pillars (EPIC-2 / EPIC-4), whose REST endpoints DO NOT EXIST YET. Per Rule 10's
 * "REST service that needs data the backend doesn't expose" carve-out, this service
 * returns a STRUCTURED-EMPTY envelope (`available: false` + an `errorCode`) rather than
 * fabricating values or delegating to a `Mock*` class — the dashboard renders graceful
 * "—" cards / rows and surfaces the errorCode via Rule 09.
 *
 * When the pillar endpoints land, swap the `of(...)` stubs below for `HttpClient` GET
 * calls (the TODOs name the expected endpoints). No consumer changes — they bind the
 * `SME_DASHBOARD_KPI` token and react to whatever the service returns.
 */
@Injectable()
export class SmeDashboardKpiRestService implements SmeDashboardKpiApi {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);

  loadKpis(): Observable<DashboardKpis> {
    // TODO(backend): GET /services/embedded/api/me/dashboard/kpis — owned by the
    // Payments + Financing pillars (EPIC-2 / EPIC-4). Until it exists, return a
    // structured-empty envelope so every card renders "—" (Rule 10 — no Mock, no
    // fabricated numbers). Replace this `of(...)` with the real `http.get<DashboardKpis>`.
    return of<DashboardKpis>({
      available: false,
      errorCode: 'kpi.notAvailableYet',
      availableCredit: null,
      activeLoans: null,
      nextPaymentDue: null,
      vibanBalance: null,
    });
  }

  loadCompliance(): Observable<ComplianceSnapshot> {
    // GET /services/embedded/api/me/dashboard/compliance — the §3.2 compliance snapshot
    // (KYC / PSD2 / GDPR / Sanctions / Stage 8), derived server-side from the party's latest
    // onboarding application + consent records. Errors propagate to the page (Rule 09).
    return this.http
      .get<ComplianceSnapshotResponse>(`${API_PATHS.ME_DASHBOARD}/compliance`)
      .pipe(
        map((res) => ({
          available: true,
          rows: (res.rows ?? []).map((r) => ({
            labelKey: `smeBorrower.dashboard.compliance.rows.${r.key}`,
            statusText: this.transloco.translate(`smeBorrower.dashboard.compliance.status.${r.status}`),
            tone: r.tone,
          })),
        })),
      );
  }
}
